import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { speechRecognitionManager } from "../../utils/speechRecognition";
import { getNavigationSuggestions } from "../../utils/azureOpenAI";
import { toast } from "react-toastify";

const Search = ({ routes }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [searchMode, setSearchMode] = useState("ask"); // "ask" or "agent"
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  useEffect(() => {
    // Set up speech recognition event handlers
    speechRecognitionManager.onResult = handleSpeechResult;
    speechRecognitionManager.onInterim = handleSpeechInterim;
    speechRecognitionManager.onError = handleSpeechError;
    speechRecognitionManager.onEnd = handleSpeechEnd;

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (showModeDropdown && !event.target.closest('.mode-dropdown-container')) {
        setShowModeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // Cleanup
      speechRecognitionManager.stopListening();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModeDropdown]);

  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setAiSuggestion(null); // Clear AI suggestion when user types
    
    if (query.length > 0) {
      const results = routes.filter((route) =>
        route.path.toLowerCase().includes(query.toLowerCase()) ||
        route.label.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredResults(results);
    } else {
      setFilteredResults([]);
    }
  };

  const handleSearchItemClick = (path) => {
    setSearchQuery("");
    setFilteredResults([]);
    setAiSuggestion(null);
    navigate(path);
  };

  const handleSpeechResult = (transcript) => {
    console.log("Speech transcript received:", transcript);
    setSearchQuery(transcript);
    setIsListening(false);
    
    // Process the speech input with AI
    processWithAI(transcript);
  };

  // Show interim (partial) speech results in real time without triggering AI
  const handleSpeechInterim = (partialTranscript) => {
    setSearchQuery(partialTranscript);
    setAiSuggestion(null);
    if (partialTranscript && partialTranscript.length > 0) {
      const results = routes.filter((route) =>
        route.path.toLowerCase().includes(partialTranscript.toLowerCase()) ||
        route.label.toLowerCase().includes(partialTranscript.toLowerCase())
      );
      setFilteredResults(results);
    } else {
      setFilteredResults([]);
    }
  };

  const handleSpeechError = (error) => {
    console.error("Speech recognition error:", error);
    setIsListening(false);
    toast.error("Speech recognition failed. Please try again.", {
      position: toast.POSITION.TOP_CENTER,
    });
  };

  const handleSpeechEnd = () => {
    setIsListening(false);
  };

  const toggleMicrophone = () => {
    if (isListening) {
      speechRecognitionManager.stopListening();
      setIsListening(false);
    } else {
      if (speechRecognitionManager.startListening()) {
        setIsListening(true);
        setSearchQuery(""); // Clear previous input
      } else {
        toast.error("Speech recognition not available in this browser.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    }
  };

  const processWithAI = async (input) => {
    if (!input.trim()) return;

    setIsProcessingAI(true);
    try {
      const suggestion = await getNavigationSuggestions(input, routes);
      
      if (suggestion.suggestedPath && suggestion.confidence > 0.7) {
        setAiSuggestion(suggestion);
        
        // If in agent mode, automatically navigate with a simple fade transition
        if (searchMode === "agent") {
          try {
            document.body.classList.add('page-fade-transition');
          } catch {}
          setTimeout(() => {
            handleSearchItemClick(suggestion.suggestedPath);
            // Remove fade class shortly after navigation to fade in
            setTimeout(() => {
              try {
                document.body.classList.remove('page-fade-transition');
              } catch {}
            }, 250);
          }, 150);
        }
      } else {
        setAiSuggestion(null);
        toast.info("No clear navigation path found. Try being more specific.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      console.error("Error processing with AI:", error);
      toast.error("AI processing failed. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setIsProcessingAI(false);
    }
  };

  const handleAISuggestionClick = () => {
    if (aiSuggestion?.suggestedPath) {
      handleSearchItemClick(aiSuggestion.suggestedPath);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      e.preventDefault();
      processWithAI(searchQuery);
    }
  };

  return (
    <div className="enhanced-search-container">
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="input-prepend input-append">
          <div className="btn-group search-group">
            <div className="search-input-container">
              {/* Mode Dropdown Button */}
              <div className="mode-dropdown-container">
                <button
                  type="button"
                  className="mode-dropdown-btn"
                  onClick={() => setShowModeDropdown(!showModeDropdown)}
                  title={`Current mode: ${searchMode === 'agent' ? 'Agent (Auto-navigate)' : 'Ask (Manual click)'}`}
                >
                  <i className={`ri-${searchMode === 'agent' ? 'infinity-line' : 'chat-1-line'}`} style={{fontSize: '18px', color: '#000000'}}></i>
                  <span className="mode-text">{searchMode === 'agent' ? 'Agent' : 'Ask'}</span>
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                
                {showModeDropdown && (
                  <div className="mode-dropdown-menu">
                    <div 
                      className={`mode-option ${searchMode === 'ask' ? 'active' : ''}`}
                      onClick={() => {
                        setSearchMode('ask');
                        setShowModeDropdown(false);
                      }}
                    >
                      <i className="ri-chat-1-line" style={{fontSize: '20px', color: '#000000'}}></i>
                      <span>Ask Mode</span>
                      <small>Wait for user to click suggestion</small>
                    </div>
                    <div 
                      className={`mode-option ${searchMode === 'agent' ? 'active' : ''}`}
                      onClick={() => {
                        setSearchMode('agent');
                        setShowModeDropdown(false);
                      }}
                    >
                      <i className="ri-infinity-line" style={{fontSize: '20px', color: '#000000'}}></i>
                      <span>Agent Mode</span>
                      <small>Auto-navigate to suggested path</small>
                    </div>
                  </div>
                )}
              </div>
              
              <input
                ref={searchInputRef}
                className="dropdown-toggle search-query text search-input"
                type="text"
                placeholder="Type here to search or use voice input..."
                value={searchQuery}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
              />
              
              {/* Microphone Button */}
              <button
                type="button"
                className={`microphone-btn ${isListening ? 'listening' : ''}`}
                onClick={toggleMicrophone}
                title={isListening ? "Stop listening" : "Start voice input"}
                disabled={!speechRecognitionManager.isSupported()}
              >
                <i className={`ri-${isListening ? 'stop-circle-fill' : 'mic-fill'}`}></i>
                {isListening && <span className="listening-indicator"></span>}
              </button>

              {/* AI Processing Indicator */}
              {isProcessingAI && (
                <div className="ai-processing-indicator">
                  <i className="ri-ai-generate"></i>
                </div>
              )}
            </div>

            {/* AI Suggestion */}
            {aiSuggestion && (
              <div className="ai-suggestion" onClick={handleAISuggestionClick}>
                <div className="ai-suggestion-header">
                  <i className="ri-ai-generate"></i>
                  <span>AI Suggestion</span>
                  <span className="confidence">({Math.round(aiSuggestion.confidence * 100)}% match)</span>
                  <span className={`mode-badge ${searchMode}`}>
                    <i className={`ri-${searchMode === 'agent' ? 'infinity-line' : 'chat-1-line'}`} style={{fontSize: '14px', color: '#000000'}}></i>
                    {searchMode === 'agent' ? 'Agent' : 'Ask'}
                  </span>
                </div>
                                  <div className="ai-suggestion-content">
                    <div className="suggested-path">{aiSuggestion.suggestedPath}</div>
                    <div className="explanation">{aiSuggestion.explanation}</div>
                    {searchMode === 'agent' && (
                      <div className="auto-navigate-note">
                        <i className="ri-arrow-right-line"></i>
                        Auto-navigating to suggested path...
                      </div>
                    )}
                  </div>
              </div>
            )}

            {/* Search Results Dropdown */}
            {filteredResults.length > 0 && (
              <ul className="dropdown-menu search-results">
                {filteredResults.map((result, index) => (
                  <li key={index}>
                    <Link to={result.path} onClick={() => handleSearchItemClick(result.path)}>
                      <div className="item">
                        <div className="path">{result.path}</div>
                        <div className="label">{result.label}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* Search Icon */}
            <a className="search-link" href="#" onClick={(e) => e.preventDefault()}>
              <i className="ri-search-2-fill"></i>
            </a>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Search;
